/**
 * §17.3 / feat/modular-plans — metadata de planes y MÓDULOS para la UI (encuesta de
 * entrada, "Tu plan", selector à la carte de platform-admin). El backend es la fuente
 * de verdad: el acceso lo define `enabled_modules`; aquí solo describimos/precio.
 *
 * Modelo MODULAR (sin auto-bundling): cada preset pre-selecciona SOLO su(s) módulo(s).
 * Precios POR MÓDULO (solo referencia/display): CRM $2,000 · Boutique $1,000 ·
 * GastroGest $1,000 · Wellness $1,800. El COMBO COMPLETO se mantiene en $2,700
 * (beneficio temporal). NO es aditivo de cobro — el billing à la carte es follow-up.
 */
export type ModuleId = 'crm' | 'boutique' | 'gastro' | 'gym';

export const MODULE_ORDER: ModuleId[] = ['crm', 'boutique', 'gastro', 'gym'];

/** Precio de referencia (display) por módulo y precio del combo completo. */
export const MODULE_META: Record<ModuleId, { label: string; priceMxn: number }> = {
  crm:      { label: 'Flow / Proyectos', priceMxn: 2000 },
  boutique: { label: 'Boutique',        priceMxn: 1000 },
  gastro:   { label: 'GastroGest',      priceMxn: 1000 },
  gym:      { label: 'Wellness',        priceMxn: 1800 },
};

/** Combo completo (todos los módulos): beneficio temporal, NO la suma. Solo display. */
export const COMBO_PRICE_MXN = 2700;

export const fmtMxn = (v: number): string => `$${v.toLocaleString('es-MX')} MXN`;

export type PlanId = 'crm' | 'boutique' | 'gastro' | 'mixto' | 'wellness';

// Presets ofrecidos en el SELECTOR (onboarding/signup + à la carte de platform-admin):
// Flow (crm), Boutique, GastroGest, Wellness. `mixto` ya NO es un preset seleccionable
// — el combo completo se arma seleccionando los 4 módulos (platform-admin si lo piden);
// se mantiene en PLAN_META solo para DISPLAY de tenants `mixto` existentes (Tu plan).
export const PLAN_ORDER: PlanId[] = ['crm', 'boutique', 'gastro', 'wellness'];

// PRESET → módulos (1:1, sin bundling — debe coincidir con plans.ts del backend).
export const PLAN_META: Record<PlanId, {
  label: string;
  tagline: string;
  modules: ModuleId[];
  includes: string;
  priceMxn: number | null;
}> = {
  crm: {
    label: 'Flow / Proyectos',
    tagline: 'Consultoría, agencias, servicios B2B',
    modules: ['crm'],
    includes: 'Clientes, pipeline de ventas, cotizaciones, proyectos y reportes.',
    priceMxn: 2000,
  },
  boutique: {
    label: 'Boutique',
    tagline: 'Estética, spa, retail',
    modules: ['boutique'],
    includes: 'Agenda de citas, catálogo de servicios/productos, historial y venta rápida.',
    priceMxn: 1000,
  },
  gastro: {
    label: 'GastroGest',
    tagline: 'Restaurantes, cafés, bares',
    modules: ['gastro'],
    includes: 'Mesas y comanda, menú/catálogo con stock, cierre de día e insights.',
    priceMxn: 1000,
  },
  wellness: {
    label: 'Wellness',
    tagline: 'Gimnasios, yoga, pilates, crossfit, box',
    modules: ['gym'],
    includes: 'Membresías, planes, check-in y venta rápida.',
    priceMxn: 1800,
  },
  mixto: {
    label: 'Mixto — todo',
    tagline: 'Lo necesito todo',
    modules: ['crm', 'boutique', 'gastro', 'gym'],
    includes: 'Flow + Boutique + GastroGest + Wellness. Todos los módulos incluidos.',
    priceMxn: COMBO_PRICE_MXN,
  },
};

export const priceLabel = (p: PlanId): string => {
  const v = PLAN_META[p].priceMxn;
  return v == null ? 'Por definir — contáctanos' : `$${v.toLocaleString('es-MX')} MXN / mes`;
};
